import {
    ExternalYieldSourceArgs,
    MarketInformation,
    MarketInformationAssetData,
    OracleQuote,
    OracleType,
    RawMarketInformationAssetData,
    TokenListMetadata
} from "@bridgesplit/abf-sdk";
import { SortDirection } from "@bridgesplit/utils";
import Decimal from "decimal.js";

import { PrincipalCaps, StrategyExpanded } from "./strategy";
import { RewardMint, StakeAccount, VaultAddress } from "./alias";

export interface LendingVaultExpanded extends LendingVaultInfo {
    principalMetadata: TokenListMetadata;
    principalOracle: OracleQuote;
    ltvInfos: Record<string, VaultLtvInfos> | undefined; // Key is collateral mint address.
    netApy: number;
}

// VaultLtvInfos is a custom front end type used as a join between strategyTerms and marketInformation.
// Market information is not available until time lock executes. If a ltvInfo has a collateral, but no market information, market information properties will be undefined.
export interface VaultLtvInfos {
    collateralMetadata: TokenListMetadata;
    maxCollateralAllocationPct: Decimal | null;
    durationToLtvInfo: Record<string, VaultLtvInfo>; //Key is the serialized strategyDuration
}

export interface VaultLtvInfo {
    apy?: string; //If no APY is set, it is disabled, this APY comes from stratgy terms
    marketInfo: VaultLtvMarketInfo | undefined;
}

export interface VaultLtvMarketInfo {
    ltv: number;
    liquidationThreshold: number;
    oracleAccount: string;
    oracleType: OracleType;
}

export interface UserVaultPositionExpanded {
    vaultExpanded: LendingVaultExpanded;
    address: string;
    amountAvailable: UserVaultLpBalance;
    index: number;
    totalAmount: UserVaultLpBalance;
    active: boolean;
    startTime: number;
}

export interface UserVaultPnl {
    vaultPnl: number | undefined;
    mostRecentDepositOrWithdrawalTimestamp: number | undefined;
    depositHistory: ParsedLendVaultAction[] | undefined;
}

export interface UserVaultLpBalance {
    amountLp: number;
    amountPrincipal: number;
    amountUsd: number;
}

export interface LendingVaultInfo {
    vault: ParsedLendingVault;
    vaultMetadata: LendingVaultMetadataExpanded;
    vaultStrategy: StrategyExpanded;
    pendingTerms: ParsedTimeLock[];
    vaultRewardsSchedules: ParsedVaultRewardsScheduleExpanded[];
    vaultRewardsEmittedAmounts: Record<number, number>;
}

export interface LendingVaultsInfoResponse {
    lendVaults: LendingVaultInfo[];
    total: number;
}

export interface ParsedLendingVault {
    address: string;
    vaultIdentifier: string | undefined;
    nonce: string;
    manager: string;
    principalMint: string;
    lpMint: string;
    lpSupply: number;
    cumulativePrincipalDeposited: number;
    maxEarlyUnstakeFee: number;
    tags?: string; //json
    depositsEnabled: boolean;
}

export interface ParsedTimeLock {
    address: string;
    vaultAddress: string;
    initializationTimestamp: number;
    executionTimestamp: number;
    update: ParsedTermsUpdate;
    marketInformationAddress?: string;
}

export enum VaultUpdateActionType {
    UpdateStrategyMarketInformation = "updateStrategyMarketInformation",
    AddCollateral = "addCollateral",
    UpdateLtv = "updateLtv",
    RemoveCollateral = "removeCollateral",
    UpdateApy = "updateApy",
    UpdateStrategySettings = "updateStrategySettings",
    UpdateVaultSettings = "updateVaultSettings",
    CapsUpdate = "capsUpdate",
    UpdateCollateralAllocationPct = "updateCollateralAllocationPct"
}

export type ParsedTermsUpdate = {
    [VaultUpdateActionType.UpdateStrategyMarketInformation]: string | null;
    [VaultUpdateActionType.AddCollateral]: ParsedAddCollateralTimelockArgs | null;
    [VaultUpdateActionType.UpdateLtv]: ParsedUpdateAssetDataParams | null;
    [VaultUpdateActionType.RemoveCollateral]: ParsedCollateralTerms | null;
    [VaultUpdateActionType.UpdateApy]: ParsedCollateralTerms | null;
    [VaultUpdateActionType.UpdateStrategySettings]: ParsedUpdateStrategySettingsParams | null;
    [VaultUpdateActionType.UpdateVaultSettings]: ParsedUpdateVaultSettingsParams | null;
    [VaultUpdateActionType.CapsUpdate]: CapParameters | null;
    [VaultUpdateActionType.UpdateCollateralAllocationPct]: ParsedUpdateAssetDataParams | null;
};

export interface ParsedAddCollateralTimelockArgs {
    collateralTerms: ParsedCollateralTerms;
    updateAssetDataParams: ParsedUpdateAssetDataParams;
}

export interface ParsedUpdateStrategySettingsParams {
    originationsEnabled?: boolean;
    liquidityBuffer?: number;
    interestFee?: number;
    originationFee?: number;
    principalFee?: number;
    originationCap?: number;
    marketInformation?: string;
    externalYieldSourceArgs?: ExternalYieldSourceArgs;
}

export interface ParsedUpdateVaultSettingsParams {
    maxEarlyUnstakeFee: number;
}
export interface CapParameters {
    borrowCaps: PrincipalCaps;
    supplyCaps: PrincipalCaps;
    withdrawCaps: PrincipalCaps;
}

export interface ParsedCollateralTerms {
    collateralIndex: number;
    mint: string;
    terms: Record<string, number>;
}

export interface ParsedCollateralTermsExpanded extends ParsedCollateralTerms {
    metadata: TokenListMetadata | undefined;
    liquidationLtvUi: number;
    ltvUi: number;
}

export interface LendingVaultMetadata {
    name: string;
    description: string;
    managerName: string;
    managerImage: string;
    depositCap?: number;
    tags?: string[];
    bannerImage?: string;
    tokenImage?: string;
    themeColor?: string;
}

export interface LendingVaultMetadataExpanded extends LendingVaultMetadata {
    isGenesisVault: boolean;
}

export interface ParsedVaultRewardsScheduleExpanded extends ParsedVaultRewardsSchedule {
    rewardMetadata: TokenListMetadata;
}
export interface ParsedVaultRewardsSchedule {
    id: number;
    vaultAddress: string;
    rewardMint: string;
    totalWeightedStakeSupply: string;
    rewardStartTime: number;
    rewardEndTime: number;
    emissionsPerSecond: string;
    /**
     * Return emissions per second as a string to preserve precision. It is returned in lamports but stored as UI amounts.
     * We keep it in lamports format for Decimal.js calculations
     * Frontend should use Decimal.js for calculations and convert to UI amount when needed
     */
    totalEmissionsAmount: number;
    rewardIndex: string;
    scheduleIndex: number;
    emissionsClaimed: number;
    lastRewardIndexUpdateTime: number;
    durationStakeWeights: number[];
}

export interface ParsedUpdateAssetDataParams {
    assetIdentifier: string;
    quoteMint?: string;
    oracleAccount?: string;
    oracleType?: number;
    maxUncertainty?: number;
    maxAge?: number;
    ltv?: number;
    liquidationThreshold?: number;
    maxCollateralAllocationPct?: Decimal | null;
    removeCollateral?: boolean;
}

export interface MarketInformationsFilter {
    principalMints: string[];
    addresses: string[];
    verified?: boolean; //When verified is true we query the presets market information table
}

export interface ParsedMarketInformation {
    marketInformation: MarketInformation;
    marketInformationAssetInfo: MarketInformationAssetData[];
    oracleMapping: Record<string, string[]>;
}

export interface RawParsedMarketInformation {
    marketInformation: MarketInformation;
    marketInformationAssetInfo: RawMarketInformationAssetData[];
    oracleMapping: Record<string, string[]>;
}

export interface ParsedLtvData {
    principalMint: string;
    ltv: number;
    liquidationThreshold: number;
}

enum TimelockStatus {
    Pending,
    Executed,
    Cancelled
}

export interface LendingVaultTimelockFilter {
    vaultAddresses?: string[];
    timelockStatus?: TimelockStatus;
    page: number;
    pageSize: number;
    sortDirection: SortDirection;
}

export interface LendingVaultsTimelockResponse {
    timeLocks: ParsedTimeLock[];
    total: number;
}

export interface LendVaultInfoFilter {
    vaultAddresses?: string[];
    vaultIdentifiers?: string[];
    principalMints?: string[];
    lpMints?: string[];
    depositsEnabled?: boolean;
    includeRewards?: boolean;
}

export interface LendVaultsPagination {
    sortType: LendVaultSortType;
    sortDirection: SortDirection;
    page: number;
    pageSize: number;
}
export interface LendVaultStakeFilter {
    sort: StakeSortType;
    sortDirection: SortDirection;
    page: number;
    pageSize: number;
}

export type FetchLendVaultsInfoParams = LendVaultsPagination & LendVaultInfoFilter;
export type FetchLendVaultStakeParams = LendVaultStakeFilter & LendVaultInfoFilter;

export enum LendVaultSortType {
    TotalDeposits,
    WAvgApy
}

export enum StakeSortType {
    StartTime,
    EndTime,
    AmountStaked,
    WAvgApy
}

export interface FetchLendVaultsHistoryParams {
    vaultAddress: string;
    timeLookback: number;
    sampleIntervalSecs: number;
}

export interface LendingVaultsHistory {
    timestamp: number;
    cumulativeLoanVolume: number;
    cumulativeLoanCount: number;
    activeLoanCount?: number;
    principalDeployed: number;
    wAvgApy: number;
    externalRewardsApy?: number;
    utilizationRate?: number;
    netAssetValue: number;
}

export interface LendingVaultAllocationExpanded extends LendingVaultAllocation {
    collateralMetadata: TokenListMetadata;
}

export interface LendingVaultAllocation {
    collateralMint: string;
    activeLoanCount: number;
    totalPrincipalDeployed: number;
    wAvgApy: number;
    collateralDepositedUsd: number | null;
}

export type UserLendingVaultFilter = FetchLendVaultStakeParams & {
    status: StakeStatus;
    stakeDuration?: ParsedAllowedStakeDuration;
};

export enum StakeStatus {
    Active,
    Unstaked
}

export enum ParsedAllowedStakeDuration {
    ZeroDays,
    OneMonth,
    ThreeMonths,
    SixMonths,
    OneYear
}

export interface UserVaultStakePositionsResponse {
    positions: ParsedUserVaultStake[];
    total: number;
}

export interface ParsedUserVaultStake {
    stakeAddress: string;
    address: string;
    vaultAddress: string;
    amountStaked: number;
    startTime: number;
    maxUnstakeFee: number;
    endTime: number;
    wAvgApy: number;
    unstakeTime?: number;
    unstakeFeeApplied?: number;
}

export interface UserExternalRewardsFilter {
    vaultAddresses: string[];
    stakeAccounts: string[];
    stakeStatus?: number;
    rewardMints: string[];
}

export interface UserExternalRewardsPnlInfo {
    rewardMint: string;
    claimedRewards: number;
    pendingRewards: number;
}

export interface UserExternalRewardsInfoExpanded extends UserExternalRewardsPnlInfo {
    rewardMetadata: TokenListMetadata;
}

export type UserVaultExternalRewardsPnlResponse = Record<
    VaultAddress,
    Record<StakeAccount, Record<RewardMint, UserExternalRewardsInfoExpanded>>
>;

// Flattened version of UserVaultExternalRewardsPnlResponse for use in the UI
export type AggregatedUserExternalRewardsPnlResponse = Record<
    VaultAddress,
    Record<RewardMint, UserExternalRewardsInfoExpanded>
>;

export enum VaultActionType {
    Deposit = "deposit",
    Withdrawal = "withdrawal"
}

export interface ParsedLendVaultAction {
    actionType: VaultActionType;
    amount: number;
    isNewDeposit: boolean;
    isFullWithdrawal: boolean;
    timestamp: number;
    vaultAddress: string;
    transactionSignature: string;
}

export interface VaultDepositSummary {
    sumOfDeposits: number;
    sumOfWithdrawals: number;
    mostRecentDepositOrWithdrawalTimestamp?: number;
    vaultActions: ParsedLendVaultAction[];
}

export type UserPositionDepositSummaryResponse = Record<string, VaultDepositSummary>;

export interface UserPositionDepositSummaryParams {
    vaultAddresses?: string[];
    stakeAccounts?: string[];
}

// Raw types
export type RawLendingVaultsInfoResponse = {
    lendVaults: {
        vault: ParsedLendingVault;
        vaultMetadata: LendingVaultMetadataExpanded;
        vaultStrategy: StrategyExpanded;
        vaultRewardsSchedules: ParsedVaultRewardsScheduleExpanded[];
        pendingTerms: Array<RawParsedTimeLock>;
        vaultRewardsEmittedAmounts: Record<number, number>;
    }[];
    total: number;
};

export type RawParsedTimeLock = Omit<ParsedTimeLock, "update"> & {
    update: RawParsedTermsUpdate;
};

export type RawParsedTermsUpdate = {
    [K in keyof ParsedTermsUpdate]: K extends
        | VaultUpdateActionType.UpdateCollateralAllocationPct
        | VaultUpdateActionType.UpdateLtv
        ? RawParsedUpdateAssetDataParams | null
        : K extends VaultUpdateActionType.AddCollateral
        ? RawParsedAddCollateralTimelockArgs | null
        : ParsedTermsUpdate[K];
};

export type RawParsedUpdateAssetDataParams = Omit<ParsedUpdateAssetDataParams, "maxCollateralAllocationPct"> & {
    maxCollateralAllocationPct?: string | null;
};

export type RawParsedAddCollateralTimelockArgs = Omit<ParsedAddCollateralTimelockArgs, "updateAssetDataParams"> & {
    updateAssetDataParams: RawParsedUpdateAssetDataParams;
};

export interface VaultApyBreakdown {
    baseApy: number;
    rewardApy: number;
    collateralYield: number;
    totalApy: number;
}
